import type { ReactNode } from "react";

/**
 * Link from inside the authenticated app to a marketing-site route.
 *
 * Always opens in a new tab so the user's tool session (in-progress
 * audit, dashboard scroll position, etc.) is preserved when they pop
 * over to read pricing / policy / blog content. Appends a small
 * external-link arrow as a visible signal, and augments the accessible
 * name with "opens in new tab" so screen-reader users aren't surprised.
 *
 * Drop-in for cases that currently use `<Link href="/pricing">…</Link>`
 * inside (app)/* routes. The visual is a normal text/inline link — the
 * arrow + new-tab affordance are the only behavioural differences.
 */
export function AppToMarketingLink({
  href,
  children,
  className,
  showIcon = true,
}: {
  /** Marketing-site route, e.g. "/pricing" or "/#pricing". */
  href: string;
  children: ReactNode;
  className?: string;
  /**
   * Whether to render the inline external-link arrow. Defaults to true.
   * Disable in contexts where the link is already adjacent to other
   * iconography that would clutter the line.
   */
  showIcon?: boolean;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      {children}
      {showIcon ? (
        <ExternalLinkIcon />
      ) : null}
      {/* SR-only string appended so the accessible name reads
          "Pricing, opens in new tab" without altering the visible label. */}
      <span className="sr-only"> (opens in new tab)</span>
    </a>
  );
}

function ExternalLinkIcon() {
  return (
    <svg
      className="app-to-marketing-icon"
      width="11"
      height="11"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4.5 2.5h-2v7h7v-2" />
      <path d="M7 2.5h2.5V5" />
      <path d="m9.5 2.5-4 4" />
    </svg>
  );
}
